import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight, X, ZoomIn, ZoomOut, Maximize, LayoutGrid, Lock, Bookmark, BookmarkCheck, Search, ListOrdered } from "lucide-react";
import { toast } from "sonner";
import { api, asList, errorText } from "../lib/api";
import SelectionMenu from "../components/SelectionMenu";
import TranslateDialog from "../components/TranslateDialog";
import { BookmarksPanel, SearchPanel, pageLabel, useSearch } from "../components/ReaderTools";
import { LogoMark } from "../components/Logo";
import { useFetch } from "../lib/useFetch";
import { PdfCanvas, usePdf } from "../components/PdfCanvas";
import { useAuth } from "../context/AuthContext";

function buildPages(issue) {
  if (!issue) return [];
  const arts = Array.isArray(issue.articles) ? issue.articles : [];
  const pages = [{ kind: "cover", issue }, { kind: "note", issue }, { kind: "contents", issue, arts }];
  arts.forEach((a) => { pages.push({ kind: "opener", a }); pages.push({ kind: "body", a }); });
  if (pages.length % 2 === 1) pages.push({ kind: "ad" });
  pages.push({ kind: "back", issue });
  return pages;
}

function LockedPage({ total, signedIn }) {
  return (
    <div className="h-full bg-[#0D1B3E] text-white p-[10%] flex flex-col justify-center gap-[5%]" data-testid="reader-locked-page">
      <Lock style={{ width: "1.6em", height: "1.6em" }} />
      <p className="f-serif text-[1.6em] leading-[1.05]">You've reached the end of the free preview.</p>
      <p className="text-[0.65em] text-[#C3CAD9] leading-relaxed">Subscribers read all {total} pages of this issue, plus every back issue and the full research archive.</p>
      <div className="flex flex-col gap-[0.4em] text-[0.62em]">
        <Link to="/subscribe" className="bg-[#002FA7] hover:bg-white hover:text-[#0D1B3E] transition-colors px-[1.2em] py-[0.8em] font-semibold text-center" data-testid="reader-subscribe-btn">See subscription plans</Link>
        {!signedIn && <Link to="/account?next=/issues" className="border border-white/40 px-[1.2em] py-[0.8em] text-center" data-testid="reader-signin-btn">Sign in</Link>}
      </div>
    </div>
  );
}

function PageInner({ p, n, doc, w, h, signedIn, text = false }) {
  if (p.kind === "pdf") return <PdfCanvas doc={doc} n={p.n} width={w} height={h} textLayer={text} />;
  if (p.kind === "locked") return <LockedPage total={p.total} signedIn={signedIn} />;
  if (p.kind === "cover") return <img src={p.issue.cover} alt="Cover" className="w-full h-full object-cover" />;
  if (p.kind === "back") return (
    <div className="h-full bg-[#0D1B3E] text-white flex flex-col items-center justify-center gap-4 p-6 text-center">
      <span className="logo-type text-[1.8em]">BPJ</span><p className="f-serif text-[1.4em]">Business Performance Journal</p><p className="f-mono text-[0.6em] text-[#BBB]">b-p-j.net · Vol. {p.issue.volume} No. {p.issue.number}</p>
    </div>
  );
  const foot = <p className="absolute bottom-[4%] left-[7%] right-[7%] flex justify-between f-mono text-[0.5em] text-[#777]"><span>BPJ</span><span>{n}</span></p>;
  if (p.kind === "note") return (
    <div className="p-[9%] h-full relative">
      <p className="f-mono text-[0.55em] tracking-[0.2em] uppercase text-[var(--red)]">From the editor</p>
      <p className="f-serif text-[1.6em] leading-tight mt-[6%]">{p.issue.title}</p>
      <p className="f-serif text-[0.78em] leading-[1.65] mt-[6%] text-[#333] first-letter:text-[2.6em] first-letter:float-left first-letter:mr-1 first-letter:leading-[0.85]">{p.issue.editor_note}</p>
      {foot}
    </div>
  );
  if (p.kind === "contents") return (
    <div className="p-[9%] h-full relative">
      <p className="f-mono text-[0.55em] tracking-[0.2em] uppercase text-[var(--red)]">Contents</p>
      <ol className="mt-[8%] space-y-[6%]">
        {p.arts.map((a, i) => (
          <li key={a.slug} className="flex gap-3"><span className="f-serif text-[1.2em] text-[var(--red)]">{String(i * 2 + 4).padStart(2, "0")}</span>
            <span><span className="block f-serif text-[0.95em] leading-snug">{a.title}</span><span className="block f-mono text-[0.5em] text-[#777] mt-1">{a.author}</span></span></li>
        ))}
      </ol>
      {foot}
    </div>
  );
  if (p.kind === "opener") return (
    <div className="h-full flex flex-col relative">
      <div className="h-[52%] overflow-hidden"><img src={p.a.image} alt="" className="w-full h-full object-cover grayscale" /></div>
      <div className="p-[8%]">
        <p className="f-mono text-[0.5em] tracking-[0.2em] uppercase text-[var(--red)]">{p.a.type === "research" ? "Peer-reviewed" : "Analysis"} · {p.a.category}</p>
        <p className="f-serif text-[1.45em] leading-[1.05] mt-[4%]">{p.a.title}</p>
        <p className="f-serif italic text-[0.72em] text-[#444] mt-[4%] leading-snug">{p.a.dek}</p>
      </div>
      {foot}
    </div>
  );
  if (p.kind === "body") return (
    <div className="p-[8%] h-full relative">
      <p className="f-mono text-[0.5em] text-[#777]">{p.a.author}, {p.a.affiliation}</p>
      <div className="columns-2 gap-[6%] mt-[5%] f-serif text-[0.6em] leading-[1.6] text-[#333] text-left hyphens-auto" lang="en">
        {(p.a.body || []).map((t, i) => <p key={i} className="mb-[0.8em]">{t}</p>)}
      </div>
      {foot}
    </div>
  );
  return (
    <div className="h-full bg-[var(--red)] text-white p-[10%] flex flex-col justify-between">
      <p className="f-mono text-[0.55em] tracking-[0.2em] uppercase">Subscribe</p>
      <p className="f-serif text-[2em] leading-[1]">Evidence, every quarter.</p>
      <p className="f-mono text-[0.55em]">b-p-j.net/subscribe</p>
    </div>
  );
}

function useViewport() {
  const [s, setS] = useState({ w: window.innerWidth, h: window.innerHeight });
  useEffect(() => {
    const on = () => setS({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener("resize", on);
    return () => window.removeEventListener("resize", on);
  }, []);
  return s;
}

export default function Reader() {
  const { issueId } = useParams();
  const { data: issue, loading } = useFetch(`/issues/${issueId}`, null);
  const { user } = useAuth();
  const pdf = usePdf(issue, user?.id || "");
  const pdfMode = Boolean(issue?.pdf_pages && pdf.doc);
  const pages = useMemo(() => {
    if (!pdfMode) return buildPages(issue);
    const list = Array.from({ length: pdf.doc.numPages }, (_, i) => ({ kind: "pdf", n: i + 1 }));
    if (!pdf.full) list.push({ kind: "locked", total: issue.pdf_pages });
    return list;
  }, [issue, pdfMode, pdf.doc, pdf.full]);
  const ratio = pdfMode ? pdf.ratio : 0.67;
  const { w, h } = useViewport();
  const single = w < 900;
  const spreads = useMemo(() => {
    if (single) return pages.map((_, i) => [i]);
    const s = [[0]];
    for (let i = 1; i < pages.length - 1; i += 2) s.push([i, i + 1].filter((x) => x < pages.length - 1));
    if (pages.length > 1) s.push([pages.length - 1]);
    return s;
  }, [pages, single]);
  const [idx, setIdx] = useState(0);
  const [dir, setDir] = useState(1);
  const [zoom, setZoom] = useState(1);
  const [thumbs, setThumbs] = useState(false);
  useEffect(() => { setIdx(0); }, [single, issueId, pdfMode]);

  const go = useCallback((d) => { setDir(d); setIdx((i) => Math.max(0, Math.min(spreads.length - 1, i + d))); }, [spreads.length]);
  useEffect(() => {
    const k = (e) => { if (e.key === "ArrowRight") go(1); if (e.key === "ArrowLeft") go(-1); };
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [go]);

  const pageH = Math.max(200, Math.min(h - 170, single ? (w - 40) / ratio : (w - 220) / 2 / ratio));
  const pageW = pageH * ratio;
  const waiting = loading || (issue?.pdf_pages && pdf.loading);
  const cur = spreads[idx] || [];
  const fs = () => { if (document.fullscreenElement) document.exitFullscreen(); else document.documentElement.requestFullscreen?.(); };
  const mode = pdfMode ? "pdf" : "web";
  const stageRef = useRef(null);
  const resumed = useRef("");
  const [panel, setPanel] = useState(null);
  const [searchSeed, setSearchSeed] = useState("");
  const [tr, setTr] = useState("");
  const [marks, setMarks] = useState([]);
  const runSearch = useSearch(pages, pdf.doc);

  const goPage = useCallback((page) => {
    const s = spreads.findIndex((sp) => sp.includes(page));
    if (s >= 0) { setDir(s > idx ? 1 : -1); setIdx(s); }
    if (single) setPanel(null);
  }, [spreads, idx, single]);

  useEffect(() => {
    if (!user || waiting || !issue) { setMarks([]); return undefined; }
    let alive = true;
    api.get(`/me/bookmarks/${issueId}`).then((r) => { if (alive) setMarks(asList(r.data)); }).catch(() => {});
    const key = `${issueId}-${mode}-${single}`;
    if (resumed.current !== key) {
      resumed.current = key;
      api.get(`/me/progress/${issueId}`).then((r) => {
        const pg = r.data?.page;
        if (!alive || r.data?.mode !== mode || !pg || pg >= pages.length) return;
        const s = spreads.findIndex((sp) => sp.includes(pg));
        if (s > 0) { setIdx(s); toast(`Welcome back — resumed at page ${pg + 1}`); }
      }).catch(() => {});
    }
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, waiting, issueId, mode, single, pages.length]);

  useEffect(() => {
    if (!user || waiting || !cur.length) return undefined;
    const t = setTimeout(() => { api.put(`/me/progress/${issueId}`, { page: cur[0], total: pages.length, mode }).catch(() => {}); }, 900);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx, user, waiting, mode]);

  const marked = marks.find((b) => b.mode === mode && cur.includes(b.page));
  const toggleMark = async () => {
    if (!user) { toast("Sign in to bookmark pages."); setPanel("bookmarks"); return; }
    try {
      if (marked) { await api.delete(`/me/bookmarks/${marked.id}`); setMarks((m) => m.filter((b) => b.id !== marked.id)); toast("Bookmark removed"); }
      else {
        const pg = cur[0];
        const { data } = await api.post("/me/bookmarks", { issue_id: issueId, page: pg, mode, label: pageLabel(pages[pg], pg) });
        setMarks((m) => [...m, data].sort((a, b) => a.page - b.page)); toast.success(`Page ${pg + 1} bookmarked`);
      }
    } catch (e) { toast.error(errorText(e)); }
  };
  const delMark = async (id) => { await api.delete(`/me/bookmarks/${id}`).catch(() => {}); setMarks((m) => m.filter((b) => b.id !== id)); };

  return (
    <div className="fixed inset-0 bg-[#0A1430] text-white flex flex-col z-30" data-testid="reader-page" data-lenis-prevent>
      <div className="h-14 shrink-0 flex items-center justify-between px-3 sm:px-5 border-b border-white/10">
        <Link to="/issues" className="flex items-center gap-3" data-testid="reader-close-btn"><LogoMark size={28} invert /><span className="hidden sm:block f-serif text-lg">{issue?.title || "Loading…"}</span></Link>
        <div className="flex items-center gap-1 text-[#DDD]">
          <span className="f-mono text-xs mr-2" data-testid="reader-page-indicator">{cur.length ? `${cur.map((x) => x + 1).join("–")} / ${pages.length}` : ""}</span>
          <button onClick={toggleMark} className={`p-2 hover:text-white ${marked ? "text-white" : ""}`} data-testid="reader-bookmark-btn" aria-label="Bookmark page">{marked ? <BookmarkCheck size={18} className="fill-[#002FA7]" /> : <Bookmark size={18} />}</button>
          <button onClick={() => setPanel((p) => (p === "bookmarks" ? null : "bookmarks"))} className="p-2 hover:text-white" data-testid="reader-bookmarks-btn" aria-label="Bookmarks"><ListOrdered size={18} /></button>
          <button onClick={() => { setSearchSeed(""); setPanel((p) => (p === "search" ? null : "search")); }} className="p-2 hover:text-white" data-testid="reader-search-btn" aria-label="Search"><Search size={18} /></button>
          <button onClick={() => setZoom((z) => (z >= 1.6 ? 1 : z + 0.3))} className="p-2 hover:text-white" data-testid="reader-zoom-btn" aria-label="Zoom">{zoom >= 1.6 ? <ZoomOut size={18} /> : <ZoomIn size={18} />}</button>
          <button onClick={() => setThumbs((v) => !v)} className="p-2 hover:text-white" data-testid="reader-thumbs-btn" aria-label="Pages"><LayoutGrid size={18} /></button>
          <button onClick={fs} className="p-2 hover:text-white hidden sm:block" data-testid="reader-fullscreen-btn" aria-label="Fullscreen"><Maximize size={18} /></button>
          <Link to="/issues" className="p-2 hover:text-white" aria-label="Close" data-testid="reader-exit-btn"><X size={20} /></Link>
        </div>
      </div>
      <div ref={stageRef} className="flex-1 relative overflow-auto grid place-items-center">
        {waiting && <p className="f-mono text-sm text-[#C3CAD9]" data-testid="reader-loading">Opening issue…</p>}
        {!loading && !issue && <p className="f-serif text-2xl">Issue not found.</p>}
        {!waiting && pages.length > 0 && (
          <motion.div key={`${idx}-${single}`} initial={{ opacity: 0, rotateY: dir * -35, x: dir * 60 }} animate={{ opacity: 1, rotateY: 0, x: 0 }}
            transition={{ duration: 0.65, ease: [0.2, 0.7, 0.2, 1] }} style={{ transformPerspective: 2200, transformOrigin: dir > 0 ? "left center" : "right center", scale: zoom }}
            className="flex origin-center" data-testid="reader-spread">
            {cur.map((pi, k) => (
              <div key={pi} className="page-paper text-[#111] relative overflow-hidden" style={{ width: pageW, height: pageH, fontSize: pageW / 18 }}>
                <PageInner p={pages[pi]} n={pi + 1} doc={pdf.doc} w={pageW} h={pageH} signedIn={Boolean(user)} text />
                {cur.length === 2 && <div className={`absolute inset-y-0 w-8 pointer-events-none ${k === 0 ? "right-0 bg-gradient-to-l" : "left-0 bg-gradient-to-r"} from-black/20 to-transparent`} />}
              </div>
            ))}
          </motion.div>
        )}
        <button onClick={() => go(-1)} disabled={idx === 0} className="absolute left-2 sm:left-6 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 grid place-items-center disabled:opacity-20" data-testid="reader-prev-btn" aria-label="Previous"><ChevronLeft /></button>
        <button onClick={() => go(1)} disabled={idx >= spreads.length - 1} className="absolute right-2 sm:right-6 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 grid place-items-center disabled:opacity-20" data-testid="reader-next-btn" aria-label="Next"><ChevronRight /></button>
        {panel === "bookmarks" && <BookmarksPanel items={marks.filter((b) => b.mode === mode)} signedIn={Boolean(user)} onGo={goPage} onDelete={delMark} onClose={() => setPanel(null)} />}
        {panel === "search" && <SearchPanel initial={searchSeed} run={runSearch} onGo={goPage} onClose={() => setPanel(null)} />}
      </div>
      <SelectionMenu containerRef={stageRef} dark onTranslate={(s) => setTr(s.text)} onSearch={(s) => { setSearchSeed(s.text.slice(0, 80)); setPanel("search"); }} />
      <TranslateDialog text={tr} onClose={() => setTr("")} />
      <div className="h-[72px] shrink-0 border-t border-white/10 px-4 flex items-center gap-4">
        <input type="range" min={0} max={Math.max(spreads.length - 1, 0)} value={idx} onChange={(e) => { const v = +e.target.value; setDir(v > idx ? 1 : -1); setIdx(v); }} className="flex-1 accent-[#002FA7]" data-testid="reader-scrubber" aria-label="Page scrubber" />
      </div>
      {thumbs && (
        <div className="absolute inset-x-0 bottom-[72px] bg-[#0D1B3E]/95 backdrop-blur-xl border-t border-white/10 p-4 flex gap-3 overflow-x-auto" data-testid="reader-thumbs">
          {spreads.map((s, i) => (
            <button key={i} onClick={() => { setDir(i > idx ? 1 : -1); setIdx(i); setThumbs(false); }} className={`shrink-0 flex gap-px p-1 ${i === idx ? "ring-2 ring-[var(--red)]" : ""}`} data-testid={`reader-thumb-${i}`}>
              {s.map((pi) => <div key={pi} className="page-paper text-[#111] overflow-hidden" style={{ width: 54 * ratio / 0.67, height: 80, fontSize: 3 }}><PageInner p={pages[pi]} n={pi + 1} doc={pdf.doc} w={54} h={80} signedIn={Boolean(user)} /></div>)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
